import {useEventsSearch, useRequiredConfig} from '@domain/shifthandover'
import {useOnPageWithoutUnmountFocus} from '@hconnect/common/hooks'
import {ShiftEvent, ShiftEventsSortBy} from '@hconnect/common/types'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {Add} from '@mui/icons-material'
import {Box, Button} from '@mui/material'
import {
  ShiftEventDetailsModes,
  getItemId,
  ShiftEventDetailsWidget as SideCard,
  TaskResolvedMandatoryCommentDialog
} from '@widgets/shift-event-details'
import {FilterOptions} from '@widgets/shift-event-filters'
import {Moment} from 'moment-timezone'
import React, {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useDraggableTaskContext} from '../../hooks/useDraggableTask'
import {usePreviousValue} from '../../hooks/usePreviousValue'
import {TasksList} from '../../layouts/TasksList'
import {Status} from '../../types/shiftHandover.types'

interface TaskBoxContainerProps {
  plantId: string
  filterOptions: FilterOptions
}

const REFETCH_INTERVAL_PERIOD_IN_MS = 180000

const TasksBox: React.FC<{
  'data-test-id': string
  title: string
  tasks?: ShiftEvent[]
  isLoading?: boolean
  withCreateButton?: boolean
  markDueDateBefore?: Moment
  section: Status
  plantId: string
}> = (props) => {
  const {
    tasks: taskList,
    section,
    title,
    isLoading,
    markDueDateBefore,
    withCreateButton,
    plantId
  } = props
  const tasks = useMemo(() => taskList && [...taskList].reverse(), [taskList])
  const [sideCardMode, setSideCardMode] = useState<ShiftEventDetailsModes | null>(null)
  const previousActiveId = usePreviousValue(sideCardMode && getItemId(sideCardMode)) || undefined
  const {draggedFrom, onDragStart, onDragOver, onDragEnd, draggingOver} = useDraggableTaskContext()
  const [isMandatoryCommentDialog, setMandatoryCommentDialog] = useState<boolean>(false)

  React.useEffect(() => {
    setSideCardMode(null)
  }, [draggedFrom])

  const {t} = useTranslation()

  const onDragEndHandler = (section: Status, taskResolvedComment: string | undefined) => {
    const isTaskResolved = section === 'done'

    if (isTaskResolved) {
      setMandatoryCommentDialog(true)
      return
    }

    onDragEnd(section, taskResolvedComment)
  }

  return (
    <Box
      data-test-id={props['data-test-id']}
      height={'100%'}
      onDragOver={(event) => {
        event.stopPropagation()
        event.preventDefault()
        onDragOver(section, () => {
          setSideCardMode(null)
        })
      }}
      onDrop={() => {
        onDragEndHandler(section, undefined)
      }}
    >
      {sideCardMode ? (
        <SideCard
          {...sideCardMode}
          setMode={(options: ShiftEventDetailsModes) => {
            if (options.mode === 'detailsMode') {
              // show the overview instead of the details
              setSideCardMode(null)
            } else {
              setSideCardMode(options)
            }
          }}
          doClose={() => {
            setSideCardMode(null)
          }}
          data-test-id={'page-tasks'}
          plantId={plantId}
        />
      ) : (
        <TasksList
          tasks={tasks}
          title={title}
          options={
            withCreateButton && (
              <Button
                variant={'text'}
                color="primary"
                data-test-id="event-create"
                onClick={() => {
                  setSideCardMode({mode: 'createMode', preSetEventType: 'task'})
                }}
                startIcon={<Add />}
              >
                {t('shiftEvent.action.createTask')}
              </Button>
            )
          }
          isLoading={isLoading}
          markDueDateBefore={markDueDateBefore}
          scrollToID={previousActiveId}
          onTaskClick={(task: ShiftEvent) => {
            setSideCardMode({mode: 'detailsMode', itemId: task.id})
          }}
          onDragStart={(task) => {
            onDragStart(section, task)
          }}
          isDragOver={draggingOver === section}
        />
      )}

      {isMandatoryCommentDialog && (
        <TaskResolvedMandatoryCommentDialog
          open={isMandatoryCommentDialog}
          onClose={() => setMandatoryCommentDialog(false)}
          onSubmit={(taskResolvedComment: string) => {
            onDragEnd(section, taskResolvedComment)
            setMandatoryCommentDialog(false)
          }}
        />
      )}
    </Box>
  )
}

export const PendingTasksBox: React.FC<TaskBoxContainerProps> = ({plantId, filterOptions}) => {
  const {t} = useTranslation()
  const config = useRequiredConfig(plantId)
  const {shifts} = config
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)

  const options: FilterOptions = {
    ...filterOptions,
    eventType: ['task'],
    status: ['pending']
  }
  const {data, isLoading, isFetching, refetch} = useEventsSearch(
    {
      plantId,
      timezone,
      shifts,
      getPlantNow,
      input: options,
      sortBy: ShiftEventsSortBy.Details,
      itemsPerPage: 50
    },
    {
      refetchInterval: REFETCH_INTERVAL_PERIOD_IN_MS,
      refetchOnWindowFocus: false
    }
  )
  useOnPageWithoutUnmountFocus(refetch)

  return (
    <TasksBox
      data-test-id="task-box-pending"
      isLoading={isFetching || isLoading}
      tasks={data?.events}
      title={`${t('shiftEvent.status.pending')}${data ? ` (${data?.events.length})` : ''}`}
      markDueDateBefore={getPlantNow().clone().startOf('day')}
      section={'pending'}
      withCreateButton
      plantId={plantId}
    />
  )
}

export const OngoingTasksBox: React.FC<TaskBoxContainerProps> = ({plantId, filterOptions}) => {
  const {t} = useTranslation()
  const config = useRequiredConfig(plantId)
  const {shifts} = config
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)

  const options: FilterOptions = {
    ...filterOptions,
    eventType: ['task'],
    status: ['ongoing']
  }
  const {data, isLoading, isFetching, refetch} = useEventsSearch(
    {
      plantId,
      timezone,
      shifts,
      getPlantNow,
      input: options,
      sortBy: ShiftEventsSortBy.Details,
      itemsPerPage: 50
    },
    {
      refetchInterval: REFETCH_INTERVAL_PERIOD_IN_MS,
      refetchOnWindowFocus: false
    }
  )
  useOnPageWithoutUnmountFocus(refetch)

  return (
    <TasksBox
      data-test-id="task-box-ongoing"
      isLoading={isFetching || isLoading}
      tasks={data?.events}
      title={`${t('shiftEvent.status.ongoing')}${data ? ` (${data?.events.length})` : ''}`}
      markDueDateBefore={getPlantNow().clone().startOf('day')}
      section={'ongoing'}
      plantId={plantId}
    />
  )
}

export const DoneTasksBox: React.FC<TaskBoxContainerProps> = ({plantId, filterOptions}) => {
  const {t} = useTranslation()
  const config = useRequiredConfig(plantId)
  const {shifts} = config
  const timezone = useUpmPlantTimezone(plantId)
  const getPlantNow = useUpmPlantNow(plantId)

  const options: FilterOptions = {
    ...filterOptions,
    eventType: ['task'],
    status: ['done']
  }

  const {
    data: tasks,
    isFetching,
    isLoading,
    refetch
  } = useEventsSearch(
    {
      plantId,
      timezone,
      shifts,
      getPlantNow,
      input: options,
      sortBy: ShiftEventsSortBy.Details,
      itemsPerPage: 50
    },
    {
      refetchInterval: REFETCH_INTERVAL_PERIOD_IN_MS,
      refetchOnWindowFocus: false
    }
  )
  useOnPageWithoutUnmountFocus(refetch)

  return (
    <TasksBox
      data-test-id="task-box-done"
      isLoading={isFetching || isLoading}
      tasks={tasks?.events}
      title={`${t('shiftEvent.status.done')}${tasks ? ` (${tasks?.events.length})` : ''}`}
      section={'done'}
      plantId={plantId}
    />
  )
}
